import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import '../../../assets/css/patient/blog.css';
import MyNavbar from '../../../components/general/navbar/MyNavbar';
import Footer from '../../../components/general/footer/Footer';
import BlogDetail from './BlogPost';
import { AppContext } from '../../../context/AppContext';

const CATEGORIES = [
  'All', 'Mental Health', 'Speech Therapy', 'Child Development',
  'Autism & Special Needs', 'Parenting Tips', 'Therapy Techniques',
  'Wellness & Lifestyle', 'Research & Studies',
];

const cleanText = (str) => (str || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

const getExcerpt = (post) => {
  const excerptClean = cleanText(post.excerpt);
  if (excerptClean.length > 3) return excerptClean;

  const contentClean = cleanText(post.content);
  if (contentClean.length > 3) {
    return contentClean.length > 140 ? contentClean.slice(0, 140) + '...' : contentClean;
  }

  return 'No summary added for this article yet.';
};

const Blog = () => {
  const { baseUrl } = useContext(AppContext);

  const [searchTerm, setSearchTerm]           = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage]         = useState(1);
  const [openPostId, setOpenPostId]           = useState(null);

  const [posts, setPosts]       = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // ── Live search suggestions (autocomplete) ──
  const [suggestions, setSuggestions]           = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions]    = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const searchWrapperRef = useRef(null);

  const postsPerPage = 6;

  // Debounce search input (for the main grid fetch)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch posts whenever filters change
  useEffect(() => {
    setLoading(true);
    setError(null);

    axios
      .get(`${baseUrl}/Blogs`, {
        params: {
          category: selectedCategory,
          search: debouncedSearch,
          page: currentPage,
          per_page: postsPerPage,
        },
      })
      .then((res) => {
        setPosts(res.data.data || []);
        setTotalPages(res.data.last_page || 1);
      })
      .catch((err) => {
        console.error('Blog fetch error:', err);
        setError('Could not load blog posts.');
      })
      .finally(() => setLoading(false));

    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [baseUrl, selectedCategory, debouncedSearch, currentPage]);

  // Fetch live suggestions as the user types (separate, faster debounce)
  useEffect(() => {
    const term = searchTerm.trim();

    if (term.length < 2) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    setSuggestionsLoading(true);
    const t = setTimeout(() => {
      axios
        .get(`${baseUrl}/Blogs`, {
          params: { search: term, per_page: 6, page: 1 },
        })
        .then((res) => {
          setSuggestions(res.data.data || []);
        })
        .catch((err) => {
          console.error('Suggestion fetch error:', err);
          setSuggestions([]);
        })
        .finally(() => setSuggestionsLoading(false));
    }, 250);

    return () => clearTimeout(t);
  }, [baseUrl, searchTerm]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openSuggestion = useCallback((post) => {
    setOpenPostId(post.id);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    setSearchTerm('');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSearchKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      openSuggestion(suggestions[activeSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    }
  };

  if (openPostId !== null) {
    return (
      <BlogDetail
        postId={openPostId}
        onBack={() => {
          setOpenPostId(null);
          window.scrollTo({ top: 0, behavior: 'instant' });
        }}
        onSelectPost={(id) => {
          setOpenPostId(id);
          window.scrollTo({ top: 0, behavior: 'instant' });
        }}
      />
    );
  }

  const showFeatured =
    currentPage === 1 &&
    !debouncedSearch &&
    selectedCategory === 'All' &&
    posts.length > 0 &&
    posts[0].featured;

  const gridPosts = showFeatured ? posts.slice(1) : posts;

  return (
    <>
      <MyNavbar />
      <div className="blog-page  blog-wrapper">

        {/* ===== HERO ===== */}
        <section className="blog-hero">
          <div className="blog-hero-bg" />
          <div className="blog-hero-inner">
            <div className="blog-hero-badge">
              <span className="blog-badge-dot" />
              Latest Insights &amp; Expert Guidance
            </div>

            <h1 className="blog-hero-title">
              Transform Your<br />
              <span className="blog-hero-accent">Communication Journey</span>
            </h1>

            <p className="blog-hero-sub">
              Expert articles on AI-powered speech therapy, Quranic healing, and holistic wellness
            </p>

            <div className="blog-hero-search" ref={searchWrapperRef}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" className="blog-search-icon">
                <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                placeholder="Search articles, topics, or keywords..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSuggestions(true);
                  setActiveSuggestionIndex(-1);
                }}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                onKeyDown={handleSearchKeyDown}
                className="blog-search-input"
                autoComplete="off"
              />
              {searchTerm && (
                <button
                  className="blog-search-clear"
                  onClick={() => {
                    setSearchTerm('');
                    setSuggestions([]);
                    setShowSuggestions(false);
                  }}
                >
                  ✕
                </button>
              )}

              {/* ── Live suggestions dropdown ── */}
              {showSuggestions && searchTerm.trim().length >= 2 && (
                <div className="blog-suggestions-dropdown">
                  {suggestionsLoading ? (
                    <div className="blog-suggestion-empty">Searching…</div>
                  ) : suggestions.length > 0 ? (
                    suggestions.map((post, i) => (
                      <div
                        key={post.id}
                        className={`blog-suggestion-item ${i === activeSuggestionIndex ? 'blog-suggestion-active' : ''}`}
                        onMouseDown={() => openSuggestion(post)}
                        onMouseEnter={() => setActiveSuggestionIndex(i)}
                      >
                        <img
                          src={post.thumbnail_url || post.thumbnail}
                          alt={post.title}
                          className="blog-suggestion-thumb"
                        />
                        <div className="blog-suggestion-meta">
                          <span className="blog-suggestion-title">{post.title}</span>
                          <span className="blog-suggestion-cat">{post.category}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="blog-suggestion-empty">No matching articles</div>
                  )}
                </div>
              )}
            </div>

            <div className="blog-hero-tags">
              <span className="blog-hero-tag">🎙️ AI-Powered Insights</span>
              <span className="blog-hero-tag">📖 Quranic Healing</span>
              <span className="blog-hero-tag">👨‍⚕️ Expert Guidance</span>
            </div>
          </div>
        </section>

        {/* ===== CATEGORY FILTERS ===== */}
        <section className="blog-filters-section">
          <div className="blog-filters-inner">
            <div className="blog-filters">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  className={`blog-filter-btn ${selectedCategory === cat ? 'blog-filter-active' : ''}`}
                  onClick={() => { setSelectedCategory(cat); setCurrentPage(1); }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ===== BLOG POSTS ===== */}
        <section className="blog-posts-section">
          <div className="blog-posts-inner">

            {loading ? (
              <div className="blog-no-results">
                <p>Loading articles...</p>
              </div>
            ) : error ? (
              <div className="blog-no-results">
                <p>{error}</p>
              </div>
            ) : (
              <>
                {/* Featured Post */}
                {showFeatured && (
                  <div
                    className="blog-featured"
                    onClick={() => setOpenPostId(posts[0].id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="blog-featured-img">
                      <img src={posts[0].thumbnail_url || posts[0].thumbnail} alt={posts[0].title} />
                      <span className="blog-featured-badge">⭐ Featured</span>
                      <span className="blog-cat-badge">{posts[0].category}</span>
                    </div>
                    <div className="blog-featured-body">
                      <div className="blog-featured-meta">
                        <span>👤 {posts[0].author}</span>
                        <span>📅 {(posts[0].published_at || posts[0].created_at || '').toString().split('T')[0]}</span>
                        <span>⏱️ {posts[0].read_time} min read</span>
                      </div>
                      <h2 className="blog-featured-title">{posts[0].title}</h2>
                      <p className="blog-featured-excerpt">{getExcerpt(posts[0])}</p>
                      <button
                        className="blog-read-btn"
                        onClick={(e) => { e.stopPropagation(); setOpenPostId(posts[0].id); }}
                      >
                        Read Full Article →
                      </button>
                    </div>
                  </div>
                )}

                {/* Posts Grid */}
                <div className="blog-grid">
                  {gridPosts.map((post) => (
                    <div
                      key={post.id}
                      className="blog-card"
                      onClick={() => setOpenPostId(post.id)}
                    >
                      <div className="blog-card-img">
                        <img src={post.thumbnail_url || post.thumbnail} alt={post.title} />
                        <span className="blog-cat-badge">{post.category}</span>
                      </div>
                      <div className="blog-card-body">
                        <h3 className="blog-card-title">{post.title}</h3>
                        <p className="blog-card-excerpt">{getExcerpt(post)}</p>
                        <div className="blog-card-meta">
                          <span>👤 {post.author}</span>
                          <span>📅 {(post.published_at || post.created_at || '').toString().split('T')[0]}</span>
                        </div>
                        <div className="blog-card-footer">
                          <span className="blog-read-time">⏱️ {post.read_time} min read</span>
                          <button
                            className="blog-read-link"
                            onClick={(e) => { e.stopPropagation(); setOpenPostId(post.id); }}
                          >
                            Read More →
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* No Results */}
                {posts.length === 0 && (
                  <div className="blog-no-results">
                    <div className="blog-no-icon">🔍</div>
                    <h3>No articles found</h3>
                    <p>Try adjusting your search or filter criteria</p>
                    <button
                      className="blog-reset-btn"
                      onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}
                    >
                      Reset Filters
                    </button>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="blog-pagination">
                    <button
                      onClick={() => setCurrentPage((p) => p - 1)}
                      disabled={currentPage === 1}
                      className="blog-page-btn"
                    >
                      ← Previous
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`blog-page-btn ${currentPage === i + 1 ? 'blog-page-active' : ''}`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage === totalPages}
                      className="blog-page-btn"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Blog;